import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, LogOut } from "lucide-react";
import { useLanguage } from "../components/ui/LanguageProvider";
import { ROLE_LABEL, ROLE_LABEL_EN, useAuth } from "../components/auth/AuthContext";
import { useConfirm } from "../components/ui/ConfirmDialog";
import Spinner from "../components/ui/Spinner";
import { isIos, isStandalone, useInstallPrompt } from "./installPrompt";
import { useAutomationMenuGroups } from "../pages/automation/AutomationLayout";
import { useSettingsSections } from "../pages/settings/useSettingsSections";

export type MobileMenuGroup = { key: string; label: string; items: { key: string; label: string; path: string }[] };

/**
 * 더보기 — 프로필 · 전체 메뉴(CRM · IT-CONSULTING · 설정, 섹션별) · 로그아웃.
 * CRM 메뉴는 CRMLayout 이 권한·사용여부까지 거른 사이드바 구조(groups)를 받고,
 * IT-CONSULTING·설정은 각 레이아웃 사이드바와 같은 훅으로 만든다(같은 권한 검사).
 * 로그아웃은 AccountMenu 와 같은 호출. 마이페이지·설정은 AccountMenu 와 같은 경로.
 */
export default function MobileMore({ groups }: { groups: MobileMenuGroup[] }) {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { name, email, role, picture } = useAuth();
  const [imgError, setImgError] = React.useState(false);
  const [canInstall, install] = useInstallPrompt();
  const standalone = isStandalone();
  const automationGroups = useAutomationMenuGroups();
  const settingsGroups = useSettingsSections().map((sec) => ({ key: `settings-${sec.key}`, label: sec.label, items: sec.items.map((it) => ({ key: it.key, label: it.label, path: it.path })) }));
  const areas = [
    { key: "crm", label: "CRM", groups },
    { key: "it", label: "IT-CONSULTING", groups: automationGroups },
    { key: "settings", label: t("설정", "Settings"), groups: settingsGroups },
  ].filter((a) => a.groups.length > 0);
  const roleLabel = role ? (language === "en" ? ROLE_LABEL_EN[role] : ROLE_LABEL[role]) : "";

  const [loggingOut, setLoggingOut] = React.useState(false);
  const logout = async () => {
    if (loggingOut) return;
    const ok = await confirm({ title: t("로그아웃할까요?", "Log out?"), confirmLabel: t("로그아웃", "Log out"), tone: "danger" });
    if (!ok) return;
    setLoggingOut(true); // 페이지를 떠나므로 되돌리지 않는다
    try { await fetch("/api/auth/logout", { method: "POST", credentials: "include" }); } finally { window.location.href = "/login"; }
  };

  const row = "flex min-h-[48px] w-full items-center gap-3 border-0 border-b border-solid border-hairline bg-canvas px-4 text-left text-body-lg text-ink active:bg-surface-hover";
  return (
    <div className="ds-scope pb-6">
      <div className="flex items-center gap-3 border-0 border-b border-solid border-hairline bg-canvas px-4 py-4">
        <span className="grid h-12 w-12 flex-shrink-0 place-items-center overflow-hidden rounded-pill bg-primary-subtle text-title-sm font-semibold text-primary-strong" aria-hidden>
          {picture && !imgError ? (
            <img src={picture} alt="" referrerPolicy="no-referrer" onError={() => setImgError(true)} className="block h-full w-full object-cover" />
          ) : (
            (name || email || "?").trim().charAt(0).toUpperCase()
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-body-lg font-semibold text-ink">{name || email || t("게스트", "Guest")}</div>
          <div className="truncate text-small text-ink-muted">{[roleLabel, email].filter(Boolean).join(" · ")}</div>
        </div>
      </div>

      {areas.map((area) => (
        <div key={area.key}>
          <div className="mt-4 bg-surface-soft px-4 py-2 text-body font-bold text-ink" data-crm-i18n-skip="true">{area.label}</div>
          {area.groups.map((g) => (
            <section key={g.key}>
              <h2 className="m-0 px-4 pb-1 pt-4 text-small font-semibold text-ink-muted" data-crm-i18n-skip="true">{g.label}</h2>
              <ul className="m-0 list-none p-0">
                {g.items.map((m) => (
                  <li key={m.key}>
                    <button type="button" onClick={() => navigate(m.path)} className={row} data-crm-i18n-skip="true">
                      <span className="min-w-0 flex-1 truncate">{m.label}</span>
                      <ChevronRight size={16} className="flex-shrink-0 text-ink-disabled" aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      ))}

      {!standalone && (canInstall || isIos()) ? (
        <section>
          <h2 className="m-0 px-4 pb-1 pt-5 text-small font-semibold text-ink-muted">{t("앱", "App")}</h2>
          {canInstall ? (
            <button type="button" onClick={() => void install()} className={row}>
              <span className="flex-1">{t("홈 화면에 앱 설치", "Install app")}</span>
              <ChevronRight size={16} className="text-ink-disabled" aria-hidden />
            </button>
          ) : (
            <p className="m-0 border-0 border-b border-solid border-hairline bg-canvas px-4 py-3 text-body text-ink-data">
              {t("Safari 공유 버튼 → \"홈 화면에 추가\"를 누르면 앱처럼 열 수 있어요.", "In Safari, tap Share → \"Add to Home Screen\" to open it like an app.")}
            </p>
          )}
        </section>
      ) : null}

      <section>
        <h2 className="m-0 px-4 pb-1 pt-5 text-small font-semibold text-ink-muted">{t("계정", "Account")}</h2>
        <ul className="m-0 list-none p-0">
          <li><button type="button" onClick={() => navigate("/settings/profile")} className={row}><span className="flex-1">{t("마이페이지", "My page")}</span><ChevronRight size={16} className="text-ink-disabled" aria-hidden /></button></li>
        </ul>
      </section>

      <div className="px-4 pt-6">
        <button type="button" onClick={logout} disabled={loggingOut} aria-busy={loggingOut || undefined} className="flex h-12 w-full items-center justify-center gap-2 rounded-control border border-solid border-hairline-strong bg-canvas text-body-lg font-medium text-danger-strong active:bg-danger-subtle">
          {loggingOut ? <Spinner size={16} /> : <LogOut size={16} aria-hidden />}
          {loggingOut ? t("로그아웃 중…", "Logging out…") : t("로그아웃", "Log out")}
        </button>
      </div>
    </div>
  );
}
